/**
 * Reseller constraint derivation and evaluation.
 *
 * A reseller's scope acts as the "parent" — child scopes (reseller's customers)
 * are restricted to what the parent scope has selected. Constraints are derived
 * automatically from the parent scope's field values.
 */

export interface ResellerConstraint {
  type: 'locked' | 'allowed_values'
  value?: unknown           // for 'locked' — the fixed value
  allowed?: unknown[]       // for 'allowed_values' — list of permitted values
  parentField: string       // human-readable field name
}

export type ConstraintMap = Record<string, ResellerConstraint>

/** Tri-state fields: 'no' in parent means locked to 'no' in child. 'yes' or 'unknown' = no constraint. */
const TRI_STATE_FIELDS: Record<string, string[]> = {
  kycConfig: [
    'documentVerification', 'acceptNzCustomers', 'acceptInternationalCustomers',
    'manualKycReview',
  ],
  amlConfig: [
    'ongoingMonitoringEnabled',
  ],
  biometricsConfig: [
    'biometricsRequired',
  ],
  kybConfig: [
    'kybRequired', 'kybAmlOngoingMonitoringEnabled', 'continuousKybEnabled',
  ],
}

/** Select fields: if parent has chosen a specific value (not 'unknown'), child is locked to it. */
const SELECT_FIELDS: Record<string, string[]> = {
  generalInfo: ['platformVersion', 'integrationType'],
  amlConfig: ['vendor', 'level', 'monitoringType'],
}

/**
 * Derive constraints from a parent scope's configuration.
 * Returns a map of field paths (e.g. "amlConfig.vendor") to their constraints.
 */
export function deriveConstraints(parentScope: Record<string, unknown>): ConstraintMap {
  const constraints: ConstraintMap = {}

  const getSection = (key: string): Record<string, unknown> => {
    const raw = parentScope[key]
    if (!raw) return {}
    if (typeof raw === 'string') {
      try { return JSON.parse(raw) } catch { return {} }
    }
    return raw as Record<string, unknown>
  }

  // Tri-state fields: 'no' → locked, 'yes'/'unknown' → no constraint
  for (const [section, fields] of Object.entries(TRI_STATE_FIELDS)) {
    const sectionData = getSection(section)
    for (const field of fields) {
      const val = sectionData[field]
      if (val === 'no') {
        constraints[`${section}.${field}`] = {
          type: 'locked',
          value: 'no',
          parentField: field,
        }
      }
    }
  }

  // Select fields: specific value (not 'unknown'/empty) → locked
  for (const [section, fields] of Object.entries(SELECT_FIELDS)) {
    const sectionData = getSection(section)
    for (const field of fields) {
      const val = sectionData[field]
      if (val && val !== 'unknown' && val !== '') {
        constraints[`${section}.${field}`] = {
          type: 'locked',
          value: val,
          parentField: field,
        }
      }
    }
  }

  // AML level: if parent has 'standard', child can't pick 'enhanced' (but can pick 'na')
  const aml = getSection('amlConfig')
  if (aml.level === 'standard') {
    constraints['amlConfig.level'] = {
      type: 'allowed_values',
      allowed: ['standard', 'na'],
      parentField: 'level',
    }
  }

  return constraints
}

/**
 * Check if a value satisfies a constraint.
 * Returns the corrected value if it violates, or null if it's fine.
 */
export function enforceConstraint(
  value: unknown,
  constraint: ResellerConstraint,
): unknown | null {
  if (constraint.type === 'locked') {
    if (value !== constraint.value) return constraint.value
    return null // value is fine
  }

  if (constraint.type === 'allowed_values' && constraint.allowed) {
    if (!constraint.allowed.includes(value)) {
      return constraint.allowed[0] // default to first allowed value
    }
    return null // value is fine
  }

  return null
}

/**
 * Validate and correct scope data against reseller constraints.
 * Returns the corrected data object (mutated) and a list of corrections made.
 */
export function enforceAllConstraints(
  scopeData: Record<string, unknown>,
  constraints: ConstraintMap,
): { corrections: Array<{ field: string; from: unknown; to: unknown }> } {
  const corrections: Array<{ field: string; from: unknown; to: unknown }> = []

  for (const [path, constraint] of Object.entries(constraints)) {
    const [section, field] = path.split('.')
    if (!section || !field) continue

    let sectionObj: Record<string, unknown> = {}
    const raw = scopeData[section]
    if (typeof raw === 'string') {
      try { sectionObj = JSON.parse(raw) } catch { continue }
    } else if (raw && typeof raw === 'object') {
      sectionObj = raw as Record<string, unknown>
    } else {
      continue
    }

    const currentValue = sectionObj[field]
    const corrected = enforceConstraint(currentValue, constraint)

    if (corrected !== null) {
      corrections.push({ field: path, from: currentValue, to: corrected })
      sectionObj[field] = corrected
      scopeData[section] = typeof raw === 'string' ? JSON.stringify(sectionObj) : sectionObj
    }
  }

  return { corrections }
}

/**
 * Format a field path for display: "amlConfig.vendor" → "AML > Vendor"
 */
export function formatConstraintPath(path: string): string {
  const [section, field] = path.split('.')
  const sectionLabels: Record<string, string> = {
    generalInfo: 'General',
    kycConfig: 'KYC',
    amlConfig: 'AML',
    biometricsConfig: 'Biometrics',
    kybConfig: 'KYB',
  }
  const sectionLabel = sectionLabels[section] || section
  const fieldLabel = (field || '')
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .replace(/^\s/, '')
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim()
  return `${sectionLabel} > ${fieldLabel}`
}
